'use client';

import { useState } from 'react';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { groupTargetAudience } from '@/lib/moz/audienceSummary';
import type { SpecialtyPath } from '@/lib/moz-submission-utils';

interface SpecialtyGroupChipsProps {
  specialties: SpecialtyPath[];
  /** Makes chips removable; a group's «Усі» chip removes the whole group. */
  onRemove?: (paths: SpecialtyPath[]) => void;
  /** 'sm' for editor lists, 'md' for the public event page. */
  size?: 'sm' | 'md';
  /** Show this many chips, then a «+N» button that reveals the rest. */
  limit?: number;
}

/**
 * Selected specialties under their top-level group name. A fully selected
 * group shows one «Усі» chip instead of every specialty.
 */
export default function SpecialtyGroupChips({ specialties, onRemove, size = 'sm', limit }: SpecialtyGroupChipsProps) {
  const { t } = useLanguage();
  const [expanded, setExpanded] = useState(false);
  const allGroups = groupTargetAudience(specialties).map((group) => ({
    ...group,
    shown: group.all ? [] : group.specialties,
  }));
  const total = allGroups.reduce((n, g) => n + (g.all ? 1 : g.specialties.length), 0);
  const collapsed = limit !== undefined && !expanded && total > limit;
  // Collapsed: the first `limit` chips in catalog order; a fully selected group is one chip.
  const groups = collapsed ? takeChips(allGroups, limit) : allGroups;
  const chipBase = size === 'md'
    ? 'inline-flex items-center gap-1 px-3 py-1 text-sm rounded-full'
    : 'inline-flex items-center gap-1 px-2 py-1 text-xs rounded';

  const chip = (key: string, label: string, title: string, paths: SpecialtyPath[], strong: boolean) => (
    <span
      key={key}
      className={`${chipBase} ${strong ? 'bg-indigo-600 text-white' : 'bg-indigo-100 text-indigo-800'}`}
      title={title}
    >
      {label}
      {onRemove && (
        <button
          type="button"
          onClick={() => onRemove(paths)}
          className={`${strong ? 'hover:text-indigo-100' : 'hover:text-indigo-900'} ml-0.5`}
          aria-label={`× ${label}`}
        >
          ×
        </button>
      )}
    </span>
  );

  return (
    <div className="space-y-2">
      {groups.map((group) => (
        <div key={group.category}>
          <p className={`${size === 'md' ? 'text-sm' : 'text-xs'} font-medium text-gray-600 mb-1`}>{group.category}</p>
          <div className="flex flex-wrap gap-2">
            {group.all
              ? chip(group.category, t('eventCreation.allSpecialtiesInGroup') || 'Усі', group.category, group.specialties, true)
              : group.shown.map((sp) =>
                chip(sp.path.join(' > '), sp.name, sp.path.join(' > '), [sp], false)
              )}
          </div>
        </div>
      ))}
      {limit !== undefined && total > limit && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className={`${size === 'md' ? 'text-sm' : 'text-xs'} font-medium text-indigo-700 hover:underline`}
          aria-expanded={expanded}
        >
          {expanded ? t('courseDetails.showFewerSpecialties') : t('courseDetails.showMoreSpecialties', { count: String(total - limit) })}
        </button>
      )}
    </div>
  );
}

function takeChips<G extends { all: boolean; specialties: SpecialtyPath[]; shown: SpecialtyPath[] }>(groups: G[], limit: number): G[] {
  const out: G[] = [];
  let left = limit;
  for (const group of groups) {
    if (left <= 0) break;
    const shown = group.all ? [] : group.specialties.slice(0, left);
    out.push({ ...group, shown });
    left -= group.all ? 1 : shown.length;
  }
  return out;
}
